import { NavLink, useNavigate } from "react-router-dom";
import {
  Activity,
  BarChart3,
  Bell,
  Bookmark,
  FileText,
  ExternalLink,
  Factory,
  LayoutDashboard,
  List,
  Plug,
  Route as RouteIcon,
  Settings,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { t } from "@/lib/i18n";
import { useSavedViews } from "@/hooks/useSavedViews";
import { writeFilters } from "@/hooks/useUrlFilters";
import { useAuth } from "@/hooks/useAuth";
import { useMetabaseSettings } from "@/hooks/useMetabaseSettings";
import type { SavedView } from "@/lib/types";

export type NavEntry = {
  to: string;
  icon: typeof List;
  label: string;
  badge?: string;
  /** True wenn nur Admins sehen sollen. Viewer sieht den Eintrag dann nicht. */
  adminOnly?: boolean;
  /** Externe URL: rendert als <a target="_blank"> statt als Router-Link. */
  external?: boolean;
};

export const NAV: NavEntry[] = [
  { to: "/uebersicht", icon: LayoutDashboard, label: t("Übersicht") },
  { to: "/logs", icon: List, label: t("Logs") },
  { to: "/traces", icon: RouteIcon, label: t("Traces") },
  { to: "/zeitstrahl", icon: Activity, label: t("Zeitstrahl") },
  { to: "/anlagen", icon: Factory, label: t("Anlagen") },
  { to: "/berichte", icon: FileText, label: t("Berichte") },
  { to: "/quellen", icon: Plug, label: t("Quellen"), adminOnly: true },
  { to: "/alarmierung", icon: Bell, label: t("Alarmierung"), adminOnly: true },
  { to: "/einstellungen", icon: Settings, label: t("Einstellungen"), adminOnly: true },
];

/**
 * Sichtbare Navigationseinträge: nach Rolle gefiltert, Metabase-Link (falls
 * konfiguriert) hinter „Anlagen" eingefügt. Sidebar (Desktop) und MobileNav
 * (Bottom-Bar) teilen sich diese Liste, damit beide dasselbe zeigen.
 */
export function useNavEntries(): NavEntry[] {
  const { isAdmin } = useAuth();
  const metabase = useMetabaseSettings();

  const nav = [...NAV];
  if (metabase.enabled && metabase.url) {
    const afterFacilities = nav.findIndex((n) => n.to === "/anlagen") + 1;
    nav.splice(afterFacilities, 0, {
      to: metabase.url,
      icon: BarChart3,
      label: "Metabase",
      external: true,
    });
  }
  return nav.filter((n) => isAdmin || !n.adminOnly);
}

export function Sidebar() {
  const { views, remove } = useSavedViews();
  const navigate = useNavigate();
  const visibleNav = useNavEntries();

  const openView = (v: SavedView) => {
    const params = writeFilters(v.filters);
    navigate({ pathname: "/logs", search: params.toString() ? `?${params}` : "" });
  };

  return (
    <aside className="hidden w-[220px] shrink-0 flex-col border-r border-border bg-sidebar text-sidebar-foreground md:flex">
      <div className="flex items-center gap-2 px-4 py-4">
        <img src="/logo.png" alt="" className="h-7 w-7 rounded-md" />
        <span className="text-sm font-medium tracking-tight">Leif</span>
      </div>

      <nav className="flex flex-col gap-0.5 px-2">
        {visibleNav.map((entry) =>
          entry.external ? (
            <a
              key={entry.to}
              href={entry.to}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent/40 hover:text-sidebar-foreground"
            >
              <entry.icon className="h-4 w-4" />
              <span>{entry.label}</span>
              <ExternalLink className="ml-auto h-3.5 w-3.5 opacity-50" />
            </a>
          ) : (
            <NavLink
              key={entry.to}
              to={entry.to}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors",
                  isActive
                    ? "bg-sidebar-accent text-accent-foreground font-medium"
                    : "text-sidebar-foreground/80 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground",
                )
              }
            >
              <entry.icon className="h-4 w-4" />
              <span>{entry.label}</span>
              {entry.badge ? (
                <span className="ml-auto rounded-full bg-warning/15 px-1.5 py-0.5 text-[10px] font-medium text-warning">
                  {entry.badge}
                </span>
              ) : null}
            </NavLink>
          ),
        )}
      </nav>

      <div className="mt-6 flex flex-col gap-0.5 px-2">
        <div className="px-2.5 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">
          {t("Gespeicherte Sichten")}
        </div>
        {views.length === 0 ? (
          <div className="px-2.5 py-1 text-[11px] text-muted-foreground/70">
            {t("Filter speichern, um sie hier wiederzufinden.")}
          </div>
        ) : (
          views.map((v) => (
            <div
              key={v.id}
              className="group flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs text-sidebar-foreground/70 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground"
            >
              <button
                type="button"
                onClick={() => openView(v)}
                className="flex flex-1 items-center gap-2 truncate text-left"
              >
                <Bookmark className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{v.name}</span>
              </button>
              <button
                type="button"
                onClick={() => remove(v.id)}
                aria-label={t("Sicht löschen")}
                className="opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))
        )}
      </div>
    </aside>
  );
}
